import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  Check,
  ChevronLeft,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { devicesApi } from '@/api/endpoints/devices';
import { ApiError } from '@/api/client';
import { useFleetStore } from '@/stores/fleet';
import { cn } from '@/lib/utils';
import type { Device } from '@/api/types';

const CATEGORIES: { value: string; label: string }[] = [
  { value: '', label: 'Padrão' },
  { value: 'truck', label: 'Caminhão' },
  { value: 'tractor', label: 'Trator' },
  { value: 'crane', label: 'Guincho' },
  { value: 'offroad', label: 'Off-road' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'van', label: 'Van' },
  { value: 'car', label: 'Carro' },
  { value: 'motorcycle', label: 'Moto' },
  { value: 'bicycle', label: 'Bicicleta' },
  { value: 'boat', label: 'Embarcação' },
  { value: 'plane', label: 'Aeronave' },
  { value: 'person', label: 'Pessoa' },
  { value: 'animal', label: 'Animal' },
];

interface FormState {
  name: string;
  uniqueId: string;
  model: string;
  category: string;
  phone: string;
  apelido: string;
  photo: string;
}

const EMPTY: FormState = {
  name: '',
  uniqueId: '',
  model: '',
  category: '',
  phone: '',
  apelido: '',
  photo: '',
};

function fromDevice(device: Device): FormState {
  const a = device.attributes ?? {};
  return {
    name: device.name ?? '',
    uniqueId: device.uniqueId ?? '',
    model: device.model ?? '',
    category: device.category ?? '',
    phone: device.phone ?? '',
    apelido:
      (typeof a.apelido === 'string' && (a.apelido as string)) ||
      (typeof a.nickname === 'string' && (a.nickname as string)) ||
      device.contact ||
      '',
    photo: device.photo ?? '',
  };
}

export function AddDevicePage() {
  const params = useParams<{ id?: string }>();
  const editId = params.id ? Number(params.id) : null;
  const isEdit = editId !== null && Number.isFinite(editId);

  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const existingDevice = useFleetStore((s) => (isEdit ? s.devices.get(editId!) : undefined));
  const [form, setForm] = useState<FormState>(EMPTY);
  const [touched, setTouched] = useState(false);

  useEffect(() => {
    if (isEdit && existingDevice) {
      setForm(fromDevice(existingDevice));
    }
  }, [isEdit, existingDevice]);

  const update = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setForm((f) => ({ ...f, [key]: value }));
  };

  const errors = useMemo(() => {
    const e: Partial<Record<keyof FormState, string>> = {};
    if (!form.name.trim()) e.name = 'Obrigatório';
    if (!form.uniqueId.trim()) e.uniqueId = 'Obrigatório';
    return e;
  }, [form]);

  const valid = Object.keys(errors).length === 0;

  const buildPayload = (): Device => {
    const baseAttrs = existingDevice?.attributes ?? {};
    const attributes = {
      ...baseAttrs,
      apelido: form.apelido.trim() || undefined,
    };
    return {
      ...(existingDevice ?? { id: 0 }),
      name: form.name.trim(),
      uniqueId: form.uniqueId.trim(),
      model: form.model.trim() || undefined,
      category: form.category || undefined,
      phone: form.phone.trim() || undefined,
      contact: form.apelido.trim() || undefined,
      photo: form.photo.trim() || undefined,
      attributes,
    } as Device;
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const payload = buildPayload();
      if (isEdit && existingDevice) {
        return devicesApi.update(existingDevice.id, payload);
      }
      return devicesApi.create(payload);
    },
    onSuccess: async (saved) => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      const id = (saved as Device | undefined)?.id ?? existingDevice?.id;
      if (id) navigate(`/devices/${id}`);
      else navigate('/devices');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!existingDevice) throw new Error('Sem máquina pra excluir');
      return devicesApi.delete(existingDevice.id);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['devices'] });
      navigate('/devices');
    },
  });

  const errorText = (e: unknown) => {
    if (e instanceof ApiError) return `Servidor respondeu ${e.status} (${e.statusText || 'erro'}).`;
    if (e instanceof Error) return e.message;
    return 'Erro desconhecido.';
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    if (!valid) return;
    saveMutation.mutate();
  };

  const onConfirmDelete = () => {
    if (!existingDevice) return;
    if (!window.confirm(`Excluir a máquina "${existingDevice.name}"? Essa ação não pode ser desfeita.`)) return;
    deleteMutation.mutate();
  };

  return (
    <form onSubmit={handleSubmit} className="mx-auto max-w-2xl space-y-4 p-4 sm:p-6">
      <Link
        to={isEdit ? `/devices/${editId}` : '/settings'}
        className="-ml-2 inline-flex items-center gap-0.5 rounded-md px-2 py-1 text-sm font-medium text-primary hover:bg-primary/5"
      >
        <ChevronLeft className="h-5 w-5" />
        Voltar
      </Link>

      <div>
        <h1 className="text-xl font-bold">
          {isEdit ? 'Editar máquina' : 'Adicionar máquina'}
        </h1>
        <p className="text-sm text-muted-foreground">
          {isEdit
            ? 'Atualize os dados cadastrais. O ID único (IMEI) só deve ser alterado em caso de troca de equipamento.'
            : 'Cadastre uma nova máquina. O ID único é o IMEI ou identificador serial do rastreador.'}
        </p>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <Field label="Descrição (placa)" required error={touched ? errors.name : undefined}>
            <Input
              value={form.name}
              onChange={(e) => update('name', e.target.value)}
              placeholder="Ex: STRADA-IWI-6934 ou A05H13P2120"
              autoFocus={!isEdit}
            />
          </Field>
          <Field label="ID único (IMEI)" required error={touched ? errors.uniqueId : undefined}>
            <Input
              value={form.uniqueId}
              onChange={(e) => update('uniqueId', e.target.value)}
              placeholder="Ex: 354778341714494"
              disabled={isEdit}
            />
            {isEdit && (
              <p className="mt-1 text-[10px] text-muted-foreground">
                ID único não é editável depois do cadastro.
              </p>
            )}
          </Field>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Modelo">
              <Input
                value={form.model}
                onChange={(e) => update('model', e.target.value)}
                placeholder="CRIOPAD FULL, LUMII PRO..."
              />
            </Field>
            <Field label="Categoria">
              <select
                value={form.category}
                onChange={(e) => update('category', e.target.value)}
                className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            <Field label="Telefone (SIM)">
              <Input
                value={form.phone}
                onChange={(e) => update('phone', e.target.value)}
                placeholder="Ex: 5511935621212"
              />
            </Field>
            <Field label="Apelido">
              <Input
                value={form.apelido}
                onChange={(e) => update('apelido', e.target.value)}
                placeholder="Nome amigável"
              />
            </Field>
          </div>

          <Field label="Foto (nome de arquivo ou URL)">
            <div className="flex gap-2">
              <Input
                value={form.photo}
                onChange={(e) => update('photo', e.target.value)}
                placeholder="ex: 30565.png ou https://..."
                className="flex-1"
              />
              {form.photo && (
                <button
                  type="button"
                  onClick={() => update('photo', '')}
                  className="rounded-md border border-border px-3 text-xs font-semibold text-muted-foreground hover:bg-accent"
                  title="Limpar"
                >
                  ×
                </button>
              )}
            </div>
            <p className="mt-1 flex items-center gap-1 text-[10px] text-muted-foreground">
              <ImageIcon className="h-3 w-3" />
              Se for só nome, será servida via /photos/devices/&lt;arquivo&gt;.
            </p>
          </Field>
        </CardContent>
      </Card>

      {(saveMutation.error || deleteMutation.error) && (
        <p className="flex items-start gap-2 rounded-md bg-status-blocked/10 px-3 py-2 text-xs font-medium text-status-blocked">
          <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          {errorText(saveMutation.error ?? deleteMutation.error)}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {isEdit && existingDevice && (
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirmDelete}
            disabled={deleteMutation.isPending || saveMutation.isPending}
            className="mr-auto gap-1.5"
          >
            <Trash2 className="h-4 w-4" />
            {deleteMutation.isPending ? 'Excluindo…' : 'Excluir'}
          </Button>
        )}
        <Button
          type="button"
          variant="outline"
          onClick={() => navigate(isEdit ? `/devices/${editId}` : '/settings')}
        >
          Cancelar
        </Button>
        <Button type="submit" disabled={!valid || saveMutation.isPending} className="gap-1.5">
          <Check className="h-4 w-4" />
          {saveMutation.isPending
            ? 'Salvando…'
            : isEdit
              ? 'Salvar alterações'
              : 'Criar máquina'}
        </Button>
      </div>
    </form>
  );
}

function Field({
  label,
  required,
  error,
  children,
}: {
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1">
      <span className="flex items-center justify-between text-xs font-semibold text-muted-foreground">
        <span>
          {label}
          {required && <span className="text-status-blocked"> *</span>}
        </span>
        {error && <span className="text-status-blocked">{error}</span>}
      </span>
      <div className={cn(error && 'rounded-md ring-2 ring-status-blocked/30')}>{children}</div>
    </label>
  );
}
